import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Divider, Grid, Checkbox, FormControlLabel, useMediaQuery,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { apiUrl, downloadBlob, handleApiError } from './utils/api';
import SearchSelect from './components/SearchSelect';
import PdfPreviewDialog from './components/PdfPreviewDialog';
import { useRole } from '../auth/RoleContext';
import { visiblePlants } from '../auth/permissions';
import { formatDate } from './utils/date';
import { useT } from '../i18n/LanguageContext';

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const qty = (v) => Number(v || 0).toFixed(2);

/**
 * Marks the footer percents as blended when a settlement spans several dispatchers: with two yards on
 * one sheet the printed % is what was actually taken across both, not any one config's rate, and
 * labelling it is the difference between a footer that reconciles and one that gets queried.
 */
const blended = (invoice, word = 'blended') => ((invoice.dispatcherBreakdown || []).length > 1 ? ` ${word}` : '');

/**
 * One footer total. Desktop keeps the printed-sheet look ("GROSS: $1.00", right-aligned); a phone gets
 * label left / amount right across the full width so the numbers line up under a thumb.
 */
function TotalLine({ phone, label, value, big = false }) {
  const variant = big ? 'h6' : 'body1';
  if (!phone) return <Typography variant={variant}>{label}: {value}</Typography>;
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ width: '100%' }}>
      <Typography variant={variant}>{label}</Typography>
      <Typography variant={variant} sx={{ fontWeight: big ? 700 : 500, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</Typography>
    </Stack>
  );
}

/** Phone replacement for a table row: title + amount on top, smaller detail lines below. */
function LineCard({ title, amount, lines }) {
  return (
    <Box sx={{ py: 1.25, borderBottom: '0.5px solid #E3E9EF' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography sx={{ fontWeight: 600, fontSize: 15, minWidth: 0, overflowWrap: 'anywhere' }}>{title}</Typography>
        <Typography sx={{ fontWeight: 700, fontSize: 15, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{amount}</Typography>
      </Stack>
      {lines.filter(Boolean).map((l, i) => (
        <Typography key={i} sx={{ fontSize: 13, color: '#5A6672', overflowWrap: 'anywhere' }}>{l}</Typography>
      ))}
    </Box>
  );
}

/**
 * IVY's sub-contractor invoice screen. Pick a sub-contractor company + truck + week range,
 * generate the invoice (layout per IVYs.txt) and download it as PDF.
 */
export default function IvyInvoice({ embedded = false }) {
  const { role } = useRole();
  const t = useT();
  const isPhone = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const [companies, setCompanies] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [plants, setPlants] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [templates, setTemplates] = useState([]);   // {code,label} of the available sheets
  const [form, setForm] = useState({
    invoiceNumber: '',
    date: '',
    companyId: '',
    truckIds: [],
    plantIds: [],
    dispatcherIds: [],
    template: '',           // blank = use the dispatcher's configured sheet
    weekNo: '',
    startDate: '',
    endDate: '',
    discountPct: '',
    includeTickets: true,
  });
  const [invoices, setInvoices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    async function loadLookups() {
      try {
        const [c, t, p, d] = await Promise.all([
          axios.get(`${apiUrl}/api/master/companies`),
          axios.get(`${apiUrl}/api/master/trucks`),
          axios.get(`${apiUrl}/api/master/plants`),
          axios.get(`${apiUrl}/api/master/plant-payment-configs`),
        ]);
        setCompanies(Array.isArray(c.data) ? c.data : []);
        setTrucks(Array.isArray(t.data) ? t.data : []);
        setPlants(Array.isArray(p.data) ? p.data : []);
        setDispatchers(Array.isArray(d.data) ? d.data : []);
        // The sheet list comes straight from the registered PDF templates, so adding a dispatcher
        // sheet on the backend makes it selectable here with no frontend change.
        try {
          const t = await axios.get(`${apiUrl}/api/master/plant-payment-configs/templates`);
          setTemplates(Object.entries(t.data || {}).map(([code, label]) => ({ id: code, name: label })));
        } catch (e) { /* the dropdown simply stays empty; the configured sheet still applies */ }
      } catch (err) {
        setError(handleApiError(err, 'Failed to load companies/trucks/plants'));
      }
    }
    loadLookups();
  }, []);

  const handleChange = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  // Trucks shown in the dropdown: scoped to the selected sub-contractor (all trucks when none chosen).
  const visibleTrucks = form.companyId
    ? trucks.filter((t) => String(t.companyId) === String(form.companyId))
    : trucks;

  const params = () => ({
    companyId: form.companyId || undefined,
    // Omit truckIds entirely when none picked → backend invoices all trucks.
    truckIds: form.truckIds.length ? form.truckIds : undefined,
    // Omit when empty → backend uses every plant. Repeated as plantIds=1&plantIds=2.
    plantIds: form.plantIds.length ? form.plantIds : undefined,
    // Selecting dispatchers switches the sheet AND applies their commission / rent / fuel. With more
    // than one, each ticket is netted at its own dispatcher's rates and the first supplies the sheet.
    dispatcherIds: form.dispatcherIds.length ? form.dispatcherIds : undefined,
    template: form.template || undefined,
    startDate: form.startDate,
    endDate: form.endDate,
    weekNo: form.weekNo || undefined,
    invoiceNumber: form.invoiceNumber || undefined,
    date: form.date || undefined,
    discountPct: form.discountPct !== '' ? form.discountPct : undefined,
  });

  // PDF-only: the preview table doesn't change, the document gains a page per scanned ticket.
  const pdfParams = () => ({ ...params(), includeTickets: form.includeTickets || undefined });

  // Repeat list params as truckIds=1&truckIds=2 (Spring binds List<Long>).
  const paramsSerializer = { indexes: null };

  const validate = () => {
    if (!form.startDate || !form.endDate) { setError('Start and end dates are required'); return false; }
    return true;
  };

  const generate = async () => {
    setError('');
    setInfo('');
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/api/ivy/invoice`, { params: params(), paramsSerializer });
      const list = res.data?.invoices || [];
      setInvoices(list);
      if (!list.length) setInfo('No matching records found.');
    } catch (err) {
      setError(handleApiError(err, 'Failed to generate invoice'));
    } finally {
      setLoading(false);
    }
  };

  const pdfName = () => `${(form.invoiceNumber || 'ivy-invoices').replace(/[^\w-]+/g, '_')}.pdf`;

  const downloadPdf = async () => {
    setError('');
    if (!validate()) return;
    try {
      const res = await axios.get(`${apiUrl}/api/ivy/invoice/pdf`, { params: pdfParams(), paramsSerializer, responseType: 'blob' });
      downloadBlob(res, pdfName());
    } catch (err) {
      setError(handleApiError(err, 'Failed to download PDF'));
    }
  };

  // Preview: fetch the same settlement PDF as a blob and show it in the review dialog before printing.
  const preview = async () => {
    setError('');
    if (!validate()) return;
    try {
      const res = await axios.get(`${apiUrl}/api/ivy/invoice/pdf`, { params: pdfParams(), paramsSerializer, responseType: 'blob' });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (err) {
      setError(handleApiError(err, 'Failed to preview PDF'));
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
  };

  const clearForm = () => {
    setForm({
      invoiceNumber: '', date: '', companyId: '', truckIds: [],
      plantIds: [], dispatcherIds: [], template: '', weekNo: '', startDate: '', endDate: '', discountPct: '',
      includeTickets: true,
    });
    setInvoices(null);
    setError('');
    setInfo('');
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      {!embedded && <Typography variant="h5" gutterBottom>{t('inv.settlementsTitle')}</Typography>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label={t('inv.invoiceNumber')} value={form.invoiceNumber}
              onChange={handleChange('invoiceNumber')} fullWidth placeholder="e.g. P281" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label={t('inv.invoiceDate')} type="date" value={form.date}
              onChange={handleChange('date')} fullWidth InputLabelProps={{ shrink: true }}
              helperText={t('inv.defaultsToEnd')} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SearchSelect
              label={t('filters.subContractor')} placeholder={t('inv.searchPlaceholder')}
              options={companies}
              getOptionLabel={(c) => `${c.name || ''}${c.ownerName ? ` (${c.ownerName})` : ''}`}
              value={form.companyId}
              // Changing the sub-contractor re-scopes the truck list, so clear any trucks that no longer apply.
              onChange={(val) => setForm((f) => ({ ...f, companyId: val || '', truckIds: [] }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SearchSelect
              multiple
              label={t('filters.truck')} placeholder={t('inv.allTrucks')}
              options={visibleTrucks}
              getOptionLabel={(tr) => String(tr.number || tr.truckNumber || tr.id)}
              value={form.truckIds}
              onChange={(val) => setForm((f) => ({ ...f, truckIds: val }))}
              noOptionsText={form.companyId ? t('inv.noTrucksForSub') : t('inv.noTrucks')}
              helperText={form.companyId ? t('inv.trucksHelpSub') : t('inv.trucksHelp')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SearchSelect
              multiple
              label={t('filters.plant')} placeholder={t('inv.allPlants')}
              options={visiblePlants(role, plants)}
              getOptionLabel={(p) => p.name || p.code || ''}
              value={form.plantIds}
              onChange={(val) => setForm((f) => ({ ...f, plantIds: val }))}
              helperText={t('inv.plantsHelp')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* Multi-select because one week can be dispatched out of several yards — LSJ work run
                from both Martin Marietta and Central Texas Stone. Each ticket is still netted at its
                own dispatcher's commission and fuel rates; only the printed sheet has to be one, so
                the first pick supplies it. */}
            <SearchSelect
              multiple
              label={t('filters.dispatcher')} placeholder={t('inv.noDispatcher')}
              options={dispatchers}
              getOptionLabel={(d) => d.label || d.matcher || ''}
              value={form.dispatcherIds}
              onChange={(val) => setForm((f) => ({ ...f, dispatcherIds: val }))}
              helperText={form.dispatcherIds.length > 1
                ? t('inv.dispatchersHelpMulti')
                : t('inv.dispatchersHelp')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* Override only. Left blank, each dispatcher's saved sheet is used — which is what the
                office wants almost every week, and picking it manually every time is how the wrong
                form gets sent. This is for the one-off: trialling a layout, or a dispatcher with no
                config yet. It does not change anything saved. */}
            <SearchSelect
              label={t('inv.template')} placeholder={t('inv.templatePlaceholder')}
              options={templates}
              getOptionLabel={(tpl) => tpl.name || tpl.id || ''}
              value={form.template}
              onChange={(val) => setForm((f) => ({ ...f, template: val || '' }))}
              helperText={t('inv.templateHelp')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label={t('inv.weekNo')} type="number" value={form.weekNo}
              onChange={handleChange('weekNo')} fullWidth helperText={t('inv.weekNoHelp')} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label={t('inv.weekStart')} type="date" required value={form.startDate}
              onChange={handleChange('startDate')} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label={t('inv.weekEnd')} type="date" required value={form.endDate}
              onChange={handleChange('endDate')} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label={t('inv.discountPct')} type="number" value={form.discountPct}
              onChange={handleChange('discountPct')} fullWidth
              InputProps={{ inputProps: { min: 0, max: 100, step: 0.01 } }}
              helperText={t('inv.discountHelp')} />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Button variant="contained" onClick={generate} sx={{ flex: { xs: '1 1 100%', sm: 'none' } }}>{t('inv.generate')}</Button>
              <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={preview}
                disabled={!invoices || !invoices.length} sx={{ flex: { xs: 1, sm: 'none' } }}>{t('inv.preview')}</Button>
              <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={downloadPdf}
                disabled={!invoices || !invoices.length} sx={{ flex: { xs: 1, sm: 'none' } }}>{t('inv.pdf')}</Button>
              <Button variant="text" onClick={clearForm}>{t('common.clear')}</Button>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={!!form.includeTickets}
                    onChange={(e) => setForm((f) => ({ ...f, includeTickets: e.target.checked }))}
                  />
                )}
                label={t('inv.attachScans')}
              />
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}
      {loading && <CircularProgress />}

      {invoices && invoices.length > 1 && (
        <Typography sx={{ mb: 1, fontWeight: 600, color: '#48535F' }}>
          {t('inv.perTruck', { n: invoices.length })}
        </Typography>
      )}

      {(invoices || []).map((invoice, idx) => (
        <Paper key={invoice.truck || idx} sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
          <Typography variant={isPhone ? 'h5' : 'h4'} align="center" gutterBottom>{t('inv.invoice')}</Typography>
          <Stack alignItems={isPhone ? 'stretch' : 'flex-end'} sx={{ mb: 2 }}>
            <TotalLine phone={isPhone} label={t('inv.number')} value={invoice.invoiceNumber || '—'} />
            <TotalLine phone={isPhone} label={t('inv.date')} value={formatDate(invoice.date) || '—'} />
          </Stack>

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2">{t('inv.orderFrom')}</Typography>
          <Typography>{invoice.orderFromOwner || '—'}</Typography>
          <Typography>{invoice.orderFromCompany || '—'}</Typography>

          <Divider sx={{ my: 1 }} />
          <Stack direction="row" columnGap={6} rowGap={0.5} flexWrap="wrap" useFlexGap>
            <Typography><strong>{t('inv.truck')}:</strong> {invoice.truck || '—'}</Typography>
            <Typography><strong>{t('inv.week')}:</strong> {invoice.weekNo ?? '—'}</Typography>
            {invoice.dispatcherLabel && (
              <Typography><strong>{t('inv.dispatcher')}:</strong> {invoice.dispatcherLabel}</Typography>
            )}
          </Stack>

          {/* With a dispatcher selected the preview mirrors their sheet: one row per ticket. */}
          {invoice.dispatcherLabel && isPhone ? (
            <Box sx={{ mt: 1.5 }}>
              {(invoice.detailLines || []).map((l, i) => (
                <LineCard
                  key={`${l.ticketNo}-${i}`}
                  title={`#${l.ticketNo}`}
                  amount={money(l.amount)}
                  lines={[
                    [l.date, l.time].filter(Boolean).join(' · '),
                    (l.pickupLocation || l.deliveryLocation) && `${l.pickupLocation || '—'} → ${l.deliveryLocation || '—'}`,
                    `${l.customerJob || l.description || '—'} · ${qty(l.quantity)} ${l.unit || ''} × ${money(l.rate)}`,
                  ]}
                />
              ))}
            </Box>
          ) : invoice.dispatcherLabel ? (
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('inv.date')}</TableCell>
                    <TableCell>{t('inv.time')}</TableCell>
                    <TableCell>{t('inv.ticketNo')}</TableCell>
                    <TableCell>{t('inv.pickup')}</TableCell>
                    <TableCell>{t('inv.delivery')}</TableCell>
                    <TableCell>{t('inv.jobCode')}</TableCell>
                    <TableCell align="right">{t('inv.qty')}</TableCell>
                    <TableCell align="right">{t('inv.rate')}</TableCell>
                    <TableCell align="right">{t('inv.total')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(invoice.detailLines || []).map((l, i) => (
                    <TableRow key={`${l.ticketNo}-${i}`}>
                      <TableCell>{l.date}</TableCell>
                      <TableCell>{l.time || '—'}</TableCell>
                      <TableCell>{l.ticketNo}</TableCell>
                      <TableCell>{l.pickupLocation || '—'}</TableCell>
                      <TableCell>{l.deliveryLocation || '—'}</TableCell>
                      <TableCell>{l.customerJob || l.description || '—'}</TableCell>
                      <TableCell align="right">{qty(l.quantity)} {l.unit}</TableCell>
                      <TableCell align="right">{money(l.rate)}</TableCell>
                      <TableCell align="right">{money(l.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : isPhone ? (
            <Box sx={{ mt: 1.5 }}>
              {(invoice.lines || []).map((l, i) => (
                <LineCard
                  key={l.description || i}
                  title={l.description || '—'}
                  amount={money(l.amount)}
                  lines={[`${qty(l.quantity)} × ${money(l.priceEach)}`, l.tickets && `${t('inv.tickets')}: ${l.tickets}`]}
                />
              ))}
            </Box>
          ) : (
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('inv.tickets')}</TableCell>
                    <TableCell>{t('inv.description')}</TableCell>
                    <TableCell align="right">{t('inv.quantity')}</TableCell>
                    <TableCell align="right">{t('inv.priceEach')}</TableCell>
                    <TableCell align="right">{t('inv.amount')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(invoice.lines || []).map((l, i) => (
                    <TableRow key={l.description || i}>
                      <TableCell>{l.tickets}</TableCell>
                      <TableCell>{l.description}</TableCell>
                      <TableCell align="right">{qty(l.quantity)}</TableCell>
                      <TableCell align="right">{money(l.priceEach)}</TableCell>
                      <TableCell align="right">{money(l.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {invoice.dispatcherLabel ? (
            <>
              {/* A settlement spanning two yards nets two different commission/fuel rates, so the
                  blended footer below is only checkable against this split. */}
              {(invoice.dispatcherBreakdown || []).length > 1 && isPhone && (
                <Box sx={{ mt: 2 }}>
                  {invoice.dispatcherBreakdown.map((d) => (
                    <LineCard
                      key={d.dispatcherId}
                      title={d.label || d.matcher || '—'}
                      amount={money(d.netTotal)}
                      lines={[
                        `${t('inv.tickets')}: ${d.ticketCount} · ${t('inv.gross')}: ${money(d.gross)}`,
                        `${t('inv.commission')} ${Number(d.dispatcherPct || 0)}%: -${money(d.dispatcherDeduction)}`,
                        `${t('inv.fuel')} ${Number(d.fuelPct || 0)}%: -${money(d.fuelSurcharge)} · ${t('inv.rent')}: -${money(d.rent)}`,
                      ]}
                    />
                  ))}
                </Box>
              )}
              {(invoice.dispatcherBreakdown || []).length > 1 && !isPhone && (
                <TableContainer sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('inv.dispatcher')}</TableCell>
                        <TableCell align="right">{t('inv.tickets')}</TableCell>
                        <TableCell align="right">{t('inv.gross')}</TableCell>
                        <TableCell align="right">{t('inv.commission')}</TableCell>
                        <TableCell align="right">{t('inv.fuel')}</TableCell>
                        <TableCell align="right">{t('inv.rent')}</TableCell>
                        <TableCell align="right">{t('inv.net')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.dispatcherBreakdown.map((d) => (
                        <TableRow key={d.dispatcherId}>
                          <TableCell>{d.label || d.matcher || '—'}</TableCell>
                          <TableCell align="right">{d.ticketCount}</TableCell>
                          <TableCell align="right">{money(d.gross)}</TableCell>
                          <TableCell align="right">
                            {`${Number(d.dispatcherPct || 0)}% · -${money(d.dispatcherDeduction)}`}
                          </TableCell>
                          <TableCell align="right">
                            {`${Number(d.fuelPct || 0)}% · -${money(d.fuelSurcharge)}`}
                          </TableCell>
                          <TableCell align="right">-{money(d.rent)}</TableCell>
                          <TableCell align="right">{money(d.netTotal)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <Stack alignItems={isPhone ? 'stretch' : 'flex-end'} sx={{ mt: 2 }} spacing={0.5}>
                <TotalLine phone={isPhone} label={t('inv.totalTickets')} value={invoice.ticketCount} />
                <TotalLine phone={isPhone} label={t('inv.gross')} value={money(invoice.gross)} />
                <TotalLine
                  phone={isPhone}
                  label={`${t('inv.dispatcher')} (${Number(invoice.dispatcherPct || 0)}%${blended(invoice, t('inv.blended'))})`}
                  value={`-${money(invoice.dispatcherDeduction)}`}
                />
                <TotalLine phone={isPhone} label={t('inv.rent')} value={`-${money(invoice.rent)}`} />
                <TotalLine
                  phone={isPhone}
                  label={`${t('inv.fuel')} (${Number(invoice.fuelPct || 0)}%${blended(invoice, t('inv.blended'))})`}
                  value={`-${money(invoice.fuelSurcharge)}`}
                />
                <TotalLine phone={isPhone} big label={t('inv.total')} value={money(invoice.netTotal)} />
              </Stack>
            </>
          ) : (
            <Stack alignItems={isPhone ? 'stretch' : 'flex-end'} sx={{ mt: 2 }} spacing={0.5}>
              <TotalLine phone={isPhone} label={t('inv.amountDue')} value={money(invoice.amountDue)} />
              <TotalLine phone={isPhone} label={t('inv.discount')} value={money(invoice.discount)} />
              <TotalLine phone={isPhone} label={t('inv.subTotal')} value={money(invoice.subTotal)} />
              <TotalLine phone={isPhone} big label={t('inv.total')} value={money(invoice.total)} />
            </Stack>
          )}
        </Paper>
      ))}

      {invoices && invoices.length > 1 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack alignItems={isPhone ? 'stretch' : 'flex-end'}>
            <TotalLine
              phone={isPhone}
              big
              label={t('inv.grandTotal')}
              value={money(invoices.reduce((s, iv) => s + Number(iv.netTotal ?? iv.total ?? 0), 0))}
            />
          </Stack>
        </Paper>
      )}

      <PdfPreviewDialog
        open={!!previewUrl}
        onClose={closePreview}
        url={previewUrl}
        title="Settlement — Preview"
        fileName={pdfName()}
      />
    </Box>
  );
}
